/** Pure presentation geometry. Authored water cells remain the source of occupancy. */
export type ShorePoint = { x: number; y: number };
export type ShoreSample = ShorePoint & { nx: number; ny: number; depth: number };
type Edge = { x: number; y: number; direction: number; used?: boolean };
const directions = [[1, 0], [0, 1], [-1, 0], [0, -1]] as const;
const key = (x: number, y: number) => `${x},${y}`;

/** Trace only exposed union edges, including the banks of islands and holes.
 * At a diagonal touch, turn right to keep disconnected water bodies separate.
 */
function waterOccupancy(cells: ReadonlyArray<readonly [number, number]>, size: number): Set<string> {
  const occupied = new Set<string>();
  for (const [x, y] of cells) {
    if (Number.isFinite(x) && Number.isFinite(y)) occupied.add(key(Math.round(x / size), Math.round(y / size)));
  }
  return occupied;
}

export function waterContours(cells: ReadonlyArray<readonly [number, number]>, size: number): ShorePoint[][] {
  return traceContours(waterOccupancy(cells, size), size);
}

function traceContours(occupied: Set<string>, size: number): ShorePoint[][] {
  const edges: Edge[] = [];
  const starts = new Map<string, Edge[]>();
  const add = (x: number, y: number, direction: number) => {
    const edge = { x, y, direction };
    edges.push(edge);
    const at = key(x, y);
    const list = starts.get(at);
    if (list) list.push(edge); else starts.set(at, [edge]);
  };
  for (const at of occupied) {
    const [x, y] = at.split(',').map(Number);
    if (!occupied.has(key(x, y - 1))) add(x, y, 0);
    if (!occupied.has(key(x + 1, y))) add(x + 1, y, 1);
    if (!occupied.has(key(x, y + 1))) add(x + 1, y + 1, 2);
    if (!occupied.has(key(x - 1, y))) add(x, y + 1, 3);
  }
  const contours: ShorePoint[][] = [];
  for (const first of edges) {
    if (first.used) continue;
    const points: ShorePoint[] = [];
    let edge: Edge | undefined = first;
    while (edge && !edge.used) {
      edge.used = true;
      points.push({ x: edge.x * size, y: edge.y * size });
      const [dx, dy] = directions[edge.direction];
      const candidates: Edge[] = starts.get(key(edge.x + dx, edge.y + dy)) ?? [];
      const direction: number = edge.direction;
      edge = [1, 0, 3, 2].map(turn => candidates.find(candidate =>
        !candidate.used && candidate.direction === (direction + turn) % 4)).find(Boolean);
    }
    if (points.length < 4) continue;
    // Straight grid runs need only their endpoints before resampling.
    contours.push(points.filter((point, i) => {
      const before = points[(i + points.length - 1) % points.length];
      const after = points[(i + 1) % points.length];
      return (point.x - before.x) * (after.y - point.y) !== (point.y - before.y) * (after.x - point.x);
    }));
  }
  return contours;
}

/** Continuous world-anchored variation: no random flicker after an editor save. */
export function shoreVariation(x: number, y: number): number {
  return Math.sin(x * 3.7 + y * 2.1) * .52 + Math.sin(x * 8.3 - y * 6.7) * .28 + Math.cos(x * 14.1 + y * 11.3) * .2;
}

export function createShoreGeometry(cells: ReadonlyArray<readonly [number, number]>, size: number): { contours: ShoreSample[][]; landPatches: ShorePoint[][] } {
  const occupied = waterOccupancy(cells, size);
  const contours = traceContours(occupied, size);
  const isWater = (x: number, y: number) => occupied.has(key(Math.floor(x / size + 1e-7), Math.floor(y / size + 1e-7)));
  let perimeter = 0;
  for (const contour of contours) for (let i = 0; i < contour.length; i++) {
    const next = contour[(i + 1) % contour.length];
    perimeter += Math.hypot(next.x - contour[i].x, next.y - contour[i].y);
  }
  // Detail follows shoreline length, never the area of the painted river.
  const spacing = Math.max(.22, perimeter / 14000);
  const landPatches: ShorePoint[][] = [];
  const samples = contours.map(contour => {
    const rounded: ShorePoint[] = [];
    for (let i = 0; i < contour.length; i++) {
      const point = contour[i], before = contour[(i + contour.length - 1) % contour.length], after = contour[(i + 1) % contour.length];
      const previousLength = Math.hypot(point.x - before.x, point.y - before.y);
      const nextLength = Math.hypot(after.x - point.x, after.y - point.y);
      const radius = Math.min(1.15, previousLength * .24, nextLength * .24);
      const entry = { x: point.x + (before.x - point.x) / previousLength * radius, y: point.y + (before.y - point.y) / previousLength * radius };
      const exit = { x: point.x + (after.x - point.x) / nextLength * radius, y: point.y + (after.y - point.y) / nextLength * radius };
      const middle = { x: (entry.x + point.x * 2 + exit.x) / 4, y: (entry.y + point.y * 2 + exit.y) / 4 };
      rounded.push(entry, middle, exit);
      if ((point.x - before.x) * (after.y - point.y) - (point.y - before.y) * (after.x - point.x) > 0) {
        // Conceal only the cosmetic blue corner outside the softened bank.
        landPatches.push([point, entry, middle, exit]);
      }
    }
    const points: ShorePoint[] = [];
    for (let i = 0; i < rounded.length; i++) {
      const a = rounded[i], b = rounded[(i + 1) % rounded.length];
      const steps = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / spacing));
      for (let j = 0; j < steps; j++) points.push({ x: a.x + (b.x - a.x) * j / steps, y: a.y + (b.y - a.y) * j / steps });
    }
    return points.map((point, i) => {
      const before = points[(i + points.length - 1) % points.length], after = points[(i + 1) % points.length];
      const length = Math.hypot(after.x - before.x, after.y - before.y) || 1;
      const nx = -(after.y - before.y) / length, ny = (after.x - before.x) / length;
      let depth = .045;
      // Keep even the finest one-cell channels open; shore geometry is cosmetic.
      for (let probe = .06; probe <= .9; probe += .06) {
        if (!isWater(point.x + nx * probe, point.y + ny * probe)) break;
        depth = probe * .43;
      }
      return { ...point, nx, ny, depth: Math.min(.36, depth) };
    });
  });
  return { contours: samples, landPatches };
}

export function sampleShorelines(cells: ReadonlyArray<readonly [number, number]>, size: number): ShoreSample[][] {
  return createShoreGeometry(cells, size).contours;
}
